import '../css/FormattedText.css';
import { parseFormattedLines } from '../utils/formattedTextUtil';
import { linkifyText } from '../utils/linkify';

/**
 * Renders admin-written free text exactly as it was typed: line breaks,
 * blank lines, bullets ("-", "*", "•"), numbering ("1.", "a)") and indented
 * sub-items. Wrapped bullet text lines up under its first word rather than
 * under the marker, and URLs/emails become links.
 *
 * Plain text only — nothing in the input is treated as markup.
 *
 * @param {object} props
 * @param {string} props.text - the free text to show
 * @param {string} [props.className] - extra class for the wrapper (font,
 *   colour and line height are inherited from it)
 * @returns {JSX.Element|null} null when the text is blank
 */
function FormattedText({ text, className = '' }) {
  const lines = parseFormattedLines(text);
  if (lines.length === 0) return null;

  return (
    <div className={`ft-root ${className}`.trim()}>
      {lines.map((line, index) => {
        // The nesting level drives padding in CSS, so the step size lives in
        // one place (FormattedText.css).
        const style = line.indent ? { '--ft-indent': line.indent } : undefined;
        const key = `${index}-${line.type}`;

        // A non-breaking space gives the empty line exactly one line height.
        if (line.type === 'blank') return <div key={key} className="ft-blank" aria-hidden="true">{' '}</div>;

        if (line.type === 'text') {
          return <div key={key} className="ft-line" style={style}>{linkifyText(line.content)}</div>;
        }

        return (
          <div key={key} className="ft-item" style={style}>
            <span className="ft-marker" aria-hidden={line.type === 'bullet'}>{line.marker}</span>
            <span className="ft-content">{linkifyText(line.content)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default FormattedText;
